import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ApiClient } from '../api/client';
import ExecutorTerminalPanel from '../components/ExecutorTerminalPanel';
import ExecutorTerminalSessionDirectory, {
  compareExecutorTerminalSessions,
  executorTerminalDisplayName,
  isExecutorTerminalSessionAttachable,
  readTerminalSessionLastUsed,
  TERMINAL_SESSION_LAST_USED_STORAGE_KEY,
  type TerminalSessionLastUsed,
  type ExecutorTerminalSessionGroup
} from '../components/ExecutorTerminalSessionDirectory';
import ErrorBanner from '../components/ErrorBanner';
import TerminalDebugToggle from '../components/TerminalDebugToggle';
import {
  TERMINAL_VIEWPORT_DEBUG_QUERY_PARAM,
  terminalViewportDebugRequested
} from '../components/TerminalViewportDebug';
import type { Session, WorkerNode } from '../types';
import { reportErrorLocal } from '../utils/errors';

type Props = {
  apiClient: ApiClient;
  isAdmin?: boolean;
  canUsePrivilegedTerminal: boolean;
  setError: (message: string | null) => void;
};

function supportsTerminal(node: WorkerNode) {
  return node.capabilities?.pty_streaming === true &&
    typeof node.capabilities?.max_pty_streams === 'number' &&
    node.capabilities.max_pty_streams > 0;
}

function compareTerminalExecutors(left: WorkerNode, right: WorkerNode) {
  const leftName = executorTerminalDisplayName(left).toLocaleLowerCase();
  const rightName = executorTerminalDisplayName(right).toLocaleLowerCase();
  return leftName.localeCompare(rightName) || left.id.localeCompare(right.id);
}

export function resolveTerminalWorkspaceSelection(
  groups: ExecutorTerminalSessionGroup[],
  requestedExecutorId: string | null,
  requestedSessionId: string | null,
  lastUsedAt: TerminalSessionLastUsed = {}
) {
  const sessionGroup = requestedSessionId
    ? groups.find((group) => group.sessions.some((session) => session.id === requestedSessionId))
    : undefined;
  const executorGroup = requestedExecutorId
    ? groups.find((group) => group.executor.id === requestedExecutorId)
    : undefined;
  const mostRecent = !sessionGroup && !executorGroup
    ? groups
      .flatMap((group) => group.sessions
        .filter(isExecutorTerminalSessionAttachable)
        .map((session) => ({ group, session }))
      )
      .sort((left, right) => compareExecutorTerminalSessions(left.session, right.session, lastUsedAt))[0]
    : undefined;
  const group = sessionGroup ?? executorGroup ?? mostRecent?.group ?? groups[0] ?? null;
  const sessionId = sessionGroup
    ? requestedSessionId
    : mostRecent?.session.id ?? null;
  return { executorId: group?.executor.id ?? null, sessionId };
}

export function terminalWorkspaceSearchParams(
  current: URLSearchParams,
  executorId: string,
  sessionId: string | null
): URLSearchParams {
  const next = new URLSearchParams();
  next.set('executor', executorId);
  if (sessionId) next.set('session', sessionId);
  // The workspace deliberately canonicalizes its selection URL. Preserve the
  // opt-in terminal diagnostic flag while still dropping unrelated/stale
  // parameters, and normalize the earlier camel-case spelling on the way.
  if (terminalViewportDebugRequested(current.toString())) {
    next.set(TERMINAL_VIEWPORT_DEBUG_QUERY_PARAM, '1');
  }
  return next;
}

export default function TerminalWorkspacePage({
  apiClient,
  isAdmin = false,
  canUsePrivilegedTerminal,
  setError
}: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState<ExecutorTerminalSessionGroup[]>([]);
  const [lastUsedAt, setLastUsedAt] = useState(readTerminalSessionLastUsed);
  const [loading, setLoading] = useState(true);
  const [workspaceRefreshRevision, setWorkspaceRefreshRevision] = useState(0);
  const [localError, setLocalError] = useState<string | null>(null);
  const markedSelectionRef = useRef<string | null>(null);
  const requestedExecutorId = searchParams.get('executor');
  const requestedSessionId = searchParams.get('session');

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    try {
      const nodes = (await apiClient.get<WorkerNode[]>('/api/executors/nodes'))
        .filter(supportsTerminal)
        .sort(compareTerminalExecutors);
      const loadedGroups = await Promise.all(nodes.map(async (executor) => {
        try {
          const sessions = await apiClient.get<Session[]>(
            `/api/executors/nodes/${encodeURIComponent(executor.id)}/terminal/sessions`
          );
          return { executor, sessions } satisfies ExecutorTerminalSessionGroup;
        } catch (error) {
          return {
            executor,
            sessions: [],
            error: error instanceof Error ? error.message : 'failed to load terminal sessions'
          } satisfies ExecutorTerminalSessionGroup;
        }
      }));
      setGroups(loadedGroups);
      setLocalError(null);
      return true;
    } catch (error) {
      reportErrorLocal('Failed to load terminal workspace', error, setError, setLocalError);
      return false;
    } finally {
      setLoading(false);
    }
  }, [apiClient, setError]);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  const refreshWorkspace = useCallback(async () => {
    if (await loadWorkspace()) {
      // Deliberate user refreshes need a new one-shot WebSocket token as well
      // as fresh session metadata; otherwise only a full page reload recovers.
      setWorkspaceRefreshRevision((current) => current + 1);
    }
  }, [loadWorkspace]);

  const selection = useMemo(
    () => resolveTerminalWorkspaceSelection(groups, requestedExecutorId, requestedSessionId, lastUsedAt),
    [groups, lastUsedAt, requestedExecutorId, requestedSessionId]
  );
  const orderedGroups = useMemo(
    () => groups.map((group) => ({
      ...group,
      sessions: [...group.sessions].sort((left, right) =>
        compareExecutorTerminalSessions(left, right, lastUsedAt)
      )
    })),
    [groups, lastUsedAt]
  );
  const selectedGroup = orderedGroups.find((group) => group.executor.id === selection.executorId) ?? null;

  const markSessionUsed = useCallback((sessionId: string) => {
    const timestamp = new Date().toISOString();
    setLastUsedAt((current) => {
      const next = { ...current, [sessionId]: timestamp };
      try {
        window.localStorage.setItem(TERMINAL_SESSION_LAST_USED_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Private browsing and embedded webviews can disable local storage.
      }
      return next;
    });
  }, []);

  const navigateToTerminal = useCallback((executorId: string, sessionId: string | null) => {
    if (sessionId) markSessionUsed(sessionId);
    const next = terminalWorkspaceSearchParams(searchParams, executorId, sessionId);
    setSearchParams(next, { replace: true });
  }, [markSessionUsed, searchParams, setSearchParams]);

  useEffect(() => {
    if (loading || !selection.sessionId || markedSelectionRef.current === selection.sessionId) return;
    markedSelectionRef.current = selection.sessionId;
    markSessionUsed(selection.sessionId);
  }, [loading, markSessionUsed, selection.sessionId]);

  useEffect(() => {
    if (loading || !selection.executorId) return;
    if (
      requestedExecutorId !== selection.executorId ||
      requestedSessionId !== selection.sessionId
    ) {
      navigateToTerminal(selection.executorId, selection.sessionId);
    }
  }, [
    loading,
    navigateToTerminal,
    requestedExecutorId,
    requestedSessionId,
    selection.executorId,
    selection.sessionId
  ]);

  const updateExecutorSessions = useCallback((executorId: string, sessions: Session[]) => {
    setGroups((current) => current.map((group) =>
      group.executor.id === executorId ? { ...group, sessions, error: null } : group
    ));
  }, []);

  return (
    <section className="terminal-workspace" aria-label="Executor terminal workspace">
      <div className="terminal-workspace__intro">
        <div>
          <h2>Terminals</h2>
          <p className="status">
            Keep one browser attachment active and jump between persistent executor sessions.
          </p>
        </div>
        <div className="actions">
          <TerminalDebugToggle isAdmin={isAdmin} />
          <button type="button" className="secondary" onClick={() => void refreshWorkspace()} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>
      {localError && <ErrorBanner message={localError} onDismiss={() => setLocalError(null)} />}
      {!loading && groups.length === 0 ? (
        <div className="card full terminal-workspace__empty">
          <h3>No terminal-capable executors</h3>
          <p className="status">
            An online executor must advertise PTY streaming before terminals can be opened here.
          </p>
        </div>
      ) : (
        <div className="terminal-workspace__layout">
          <aside className="terminal-workspace__rail">
            <ExecutorTerminalSessionDirectory
              groups={orderedGroups}
              lastUsedAt={lastUsedAt}
              loading={loading}
              onRefresh={() => void refreshWorkspace()}
              onSelect={navigateToTerminal}
              selectedExecutorId={selection.executorId}
              selectedSessionId={selection.sessionId}
            />
          </aside>
          <div className="terminal-workspace__stage">
            {selectedGroup ? (
              <ExecutorTerminalPanel
                apiClient={apiClient}
                executorId={selectedGroup.executor.id}
                setError={setError}
                canUsePrivilegedTerminal={canUsePrivilegedTerminal}
                requestedSessionId={selection.sessionId}
                lastUsedAt={lastUsedAt}
                workspaceGroups={orderedGroups}
                workspaceLoading={loading}
                workspaceRefreshRevision={workspaceRefreshRevision}
                onWorkspaceRefresh={() => void refreshWorkspace()}
                onWorkspaceSelect={navigateToTerminal}
                onWorkspaceSessionsChange={updateExecutorSessions}
              />
            ) : (
              <div className="card full terminal-workspace__loading">
                {loading ? 'Loading terminal workspace...' : 'Choose an executor.'}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
